import { Injectable } from '@angular/core';
import { CompanyStorage } from 'src/app/_core/company-storage';
import { IPromo } from 'src/app/models/promo';

const PROMOS_TABLE_KEY = '__promos';

@Injectable({ providedIn: 'root' })
export class PromosRepository {
  constructor(private _companyStorage: CompanyStorage) {}

  async getPromo(promoId: number): Promise<IPromo> {
    const _allPromos = await this._getAllPromos();
    return _allPromos.find((p) => +p.id == promoId);
  }

  private async _getAllPromos(): Promise<IPromo[]> {
    const repo = await this._companyStorage.repository<IPromo>(
      PROMOS_TABLE_KEY
    );

    const promos = await repo.getItems();
    return promos || [];
  }

  async loadPromos(customers: IPromo[]) {
    const repo = await this._companyStorage.repository<IPromo>(
      PROMOS_TABLE_KEY
    );

    repo.loadItems(customers);
  }

  async clearStorage() {
    const repo = await this._companyStorage.repository<IPromo>(
      PROMOS_TABLE_KEY
    );

    repo.deleteTable();
  }

  async getAvailablePromos(
    products: { productId: number; categoryId: number }[],
    customerId?: number,
    customerPromoId?: number
  ): Promise<IPromo[]> {
    const _promos = [];
    if (!products || !products.length) {
      return _promos;
    }

    const orderProductLookup = {};
    const orderCategoryLookup = {};

    for (let p of products) {
      if (p.productId) {
        orderProductLookup[p.productId] = true;
      }

      if (p.categoryId) {
        orderCategoryLookup[p.categoryId] = true;
      }
    }

    const _allPromos = await this._getAllPromos();
    for (let promo of _allPromos) {
      if (promo.loyaltyType != 'Discount' && promo.loyaltyType != 'Bonuses') {
        continue;
      }

      if (promo.loyaltyType == 'Bonuses' && !customerId) {
        continue;
      }

      if (promo.availability == 'Personal' && customerPromoId != promo.id) {
        continue;
      }

      if (promo.availability == 'Registred' && !customerId) {
        continue;
      }

      if (promo.availability == 'NotRegistred' && customerId) {
        continue;
      }

      const from = promo.fromDate ? new Date(promo.fromDate) : null;
      const to = promo.toDate ? new Date(promo.toDate) : null;
      const now = new Date();
      const nowDay = now.getDay();
      const nowHours = now.getHours();
      const nowMinutes = now.getMinutes();

      if (from && from > now) {
        continue;
      }

      if (to && to < now) {
        continue;
      }

      if (promo.daysOfWeekJson == 'null') {
        promo.daysOfWeekJson = null;
      }

      const daysOfWeekArray: number[] = promo.daysOfWeekJson
        ? JSON.parse(promo.daysOfWeekJson)
        : [];

      if (daysOfWeekArray.length && daysOfWeekArray.indexOf(nowDay) < 0) {
        continue;
      }

      let times: any[] = [];
      if (promo.times) {
        try {
          times = JSON.parse(promo.times);
        } catch {}

        if (times && times.length) {
          const _time = times[0];

          if (_time.from) {
            const parts = _time.from.split(':');
            if (parts.length == 2) {
              const _hours = parts[0];
              const _minutes = parts[1];

              if (
                +_hours > nowHours ||
                (+_hours == nowHours && +_minutes >= nowMinutes)
              ) {
                continue;
              }
            }
          }

          if (_time.to) {
            const parts = _time.to.split(':');
            if (parts.length == 2) {
              const _hours = parts[0];
              const _minutes = parts[1];

              if (
                +_hours < nowHours ||
                (+_hours == nowHours && +_minutes < nowMinutes)
              ) {
                continue;
              }
            }
          }
        }
      }

      if (promo.percentageDiscount) {
        _promos.push(promo);
        continue;
      }

      const promoProducts = promo.products || [];
      const promoCategories = promo.categories || [];
      if (promoProducts.length || promoCategories.length) {
        const promoProduct = promoProducts.find(
          (p) => orderProductLookup[p.productId]
        );
        if (promoProducts.length && promoProduct) {
          _promos.push(promo);
          continue;
        }

        const promoCategory = promoCategories.find(
          (c) => orderCategoryLookup[c.categoryId]
        );
        if (promoCategories.length && promoCategory) {
          _promos.push(promo);
        }
      } else {
        _promos.push(promo);
      }
    }

    return _promos;
  }
}
